import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session } from '@supabase/supabase-js';

export type UserType = 'consumer' | 'business' | null;

interface Profile {
  id: string;
  email: string | null;
  name: string | null;
  user_type: 'consumer' | 'business';
  company_name: string | null;
  description: string | null;
  phone: string | null;
  contact_email: string | null;
  operating_area: string | null;
  operating_district: string | null;
  profile_image: string | null;
  is_onboarded: boolean | null;
  terms_accepted_at: string | null;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: Profile | null;
  session: Session | null;
  isAuthenticated: boolean;
  userType: UserType;
  isLoading: boolean;
  login: (email: string, password: string, userType?: UserType) => Promise<{ error: Error | null }>;
  register: (email: string, password: string, name: string, userType: UserType) => Promise<{ error: Error | null }>;
  logout: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<Profile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('Error fetching profile:', error);
      return null;
    }
    return data as Profile;
  };

  useEffect(() => {
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('Auth state changed:', event, newSession?.user?.id);
        setSession(newSession);
        
        if (newSession?.user) {
          setTimeout(async () => {
            const profile = await fetchProfile(newSession.user.id);
            setUser(profile);
            setIsLoading(false);
          }, 100);
        } else {
          setUser(null);
          setIsLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(async ({ data: { session: existingSession } }) => {
      setSession(existingSession);
      if (existingSession?.user) {
        const profile = await fetchProfile(existingSession.user.id);
        setUser(profile);
      }
      setIsLoading(false);
    });

    return () => {
      authSubscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string, _userType?: UserType): Promise<{ error: Error | null }> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      console.error('Login error:', error);
      return { error };
    }
    return { error: null };
  };

  const register = async (email: string, password: string, name: string, userType: UserType): Promise<{ error: Error | null }> => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          name,
          user_type: userType || 'consumer',
        },
      },
    });
    if (error) {
      console.error('Register error:', error);
      return { error };
    }
    return { error: null };
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return;

    const { validateProfileUpdate } = await import('@/lib/validation');
    const validation = validateProfileUpdate(updates as Record<string, unknown>);
    if (!validation.valid) {
      console.error('Profile validation failed:', validation.error);
      return;
    }

    const { error } = await supabase
      .from('profiles')
      .update(validation.sanitized as any)
      .eq('id', user.id);

    if (error) {
      console.error('Error updating profile:', error);
      return;
    }

    const updatedProfile = await fetchProfile(user.id);
    if (updatedProfile) {
      setUser(updatedProfile);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isAuthenticated: !!session,
        userType: user?.user_type || null,
        isLoading,
        login,
        register,
        logout,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
